/*
 * Iter 31.288 — SpyRadio.uk multi-station public player.
 *
 * Six's brief: replace the fixed catalogue with Drive-backed stations
 * Two curates by folder. Every subfolder under `_Filed/radio_stations/`
 * on Drive becomes a station. Each station has `tracks/` + `drops/`
 * subfolders; the player auto-shuffles the union.
 *
 * This is the PUBLIC-facing player at spyradio.uk. The internal
 * SpyFinance player at spyfinance.cloud/spyradio/player is untouched.
 *
 * Palette + type match the internal player (Two's SpyRadio brand):
 *   near-black #0D1117 · cold steel-blue #4A9EBF · Space Grotesk +
 *   JetBrains Mono.
 *
 * Playback race-fix from iter 31.286 is baked in from day one — no
 * synchronous `.play()` after `.src` change; wait for the `canplay`
 * event and swallow AbortError / NotAllowedError as expected
 * transitional states.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import AudioVisualiser from "@/components/AudioVisualiser";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 31.298 — exported so the per-station page (SpyRadioStation.jsx)
// can reuse the same palette + typography + player without duplication.
export const COLOURS = {
    bg: "#0D1117",
    bgSoft: "#161B22",
    accent: "#4A9EBF",
    accentDim: "#3A7A94",
    ink: "#E6EDF3",
    inkSoft: "#7D8590",
    line: "#21262D",
};

function _shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export function useDisplayFonts() {
    useEffect(() => {
        // Iter 31.292 — Lexend (weight 500) for the six public sites.
        // Internal-only JetBrains Mono retained for the "Now Playing" /
        // eyebrow labels — that's a Shed-radio deliberate accent, not
        // menu chrome.
        const links = [
            "https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap",
            "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap",
        ].map((href) => {
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = href;
            l.dataset.spyRadioMs = "1";
            document.head.appendChild(l);
            return l;
        });
        return () => links.forEach((l) => l.parentNode?.removeChild(l));
    }, []);
}

// ── STATION PICKER ────────────────────────────────────────────────────
export function StationCard({ station, active, onPick, deepLinkTo }) {
    const [hover, setHover] = useState(false);
    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: "relative",
                cursor: "pointer",
                background: active ? COLOURS.bgSoft : "transparent",
                border: `1px solid ${active || hover ? COLOURS.accent : COLOURS.line}`,
                overflow: "hidden",
                transition: "border-color 220ms, transform 220ms",
                transform: hover ? "translateY(-2px)" : "translateY(0)",
            }}
            data-testid={`station-card-${station.slug}`}
        >
            <button
                onClick={onPick}
                type="button"
                style={{
                    display: "block",
                    textAlign: "left",
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "inherit",
                    fontFamily: "inherit",
                }}
                data-testid={`station-card-pick-${station.slug}`}
            >
                <div
                    style={{
                        aspectRatio: "1 / 1",
                        background: station.cover_url
                            ? `url(${API}${station.cover_url}) center/cover no-repeat`
                            : `linear-gradient(135deg, ${COLOURS.bgSoft}, ${COLOURS.accentDim})`,
                        borderBottom: `1px solid ${COLOURS.line}`,
                    }}
                />
                <div style={{ padding: "14px 16px 16px" }}>
                    <div
                        style={{
                            fontFamily: "'Lexend', system-ui, sans-serif",
                            fontSize: 15,
                            fontWeight: 600,
                            color: COLOURS.ink,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {station.name}
                    </div>
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.18em",
                            color: active ? COLOURS.accent : COLOURS.inkSoft,
                            marginTop: 6,
                        }}
                    >
                        {station.track_count} track{station.track_count === 1 ? "" : "s"}
                    </div>
                </div>
            </button>
            {/* Iter 31.298 — deep-link to the per-station page. Absolute-
                positioned so it doesn't steal clicks from the pick button. */}
            {deepLinkTo ? (
                <a
                    href={deepLinkTo}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`station-card-deeplink-${station.slug}`}
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: COLOURS.accent,
                        background: "rgba(13,17,23,0.72)",
                        padding: "4px 8px",
                        border: `1px solid ${COLOURS.accent}`,
                        textDecoration: "none",
                        backdropFilter: "blur(4px)",
                    }}
                    title="Open dedicated page for this station"
                >
                    open ↗
                </a>
            ) : null}
        </div>
    );
}

// ── PLAYER SHELL ──────────────────────────────────────────────────────
function usePlayer(tracks) {
    const audioRef = useRef(null);
    const [queue, setQueue] = useState([]);      // shuffled remainder
    const [current, setCurrent] = useState(null); // current track
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [error, setError] = useState("");

    // On track list change, prime the shuffle queue.
    useEffect(() => {
        if (!tracks || tracks.length === 0) {
            setQueue([]);
            setCurrent(null);
            setPlaying(false);
            return;
        }
        const [first, ...rest] = _shuffle(tracks);
        setCurrent(first);
        setQueue(rest);
        setPlaying(true);
        setError("");
    }, [tracks]);

    // Iter 31.286a (24 Jul 2026 hotfix) — split source-loading from
    // play/pause commands. The previous single effect keyed on
    // `[current, playing]` re-ran on every `playing` flip and
    // re-set `el.src` + `el.load()` — which restarts the current
    // track from t=0. Effect 1 only touches src when `current`
    // changes; effect 2 issues play/pause commands without ever
    // reassigning src.

    // Effect 1: load new source when the current track changes.
    useEffect(() => {
        if (!current || !audioRef.current) return;
        const el = audioRef.current;
        el.crossOrigin = "anonymous";
        el.src = `${API}${current.stream_url}`;
        el.load();
    }, [current]);

    // Effect 2: honour the `playing` intent for the loaded source.
    // Waits for `canplay` if the media isn't ready yet; calls .pause()
    // when `playing` flips false without touching src.
    useEffect(() => {
        if (!current || !audioRef.current) return undefined;
        const el = audioRef.current;
        if (!playing) {
            if (!el.paused) el.pause();
            return undefined;
        }
        let cancelled = false;
        const attemptPlay = () => {
            if (cancelled) return;
            const p = el.play();
            if (p && typeof p.catch === "function") {
                p.catch((err) => {
                    if (err && (err.name === "AbortError"
                                || err.name === "NotAllowedError")) return;
                    setError(`playback blocked: ${err?.message || err}`);
                });
            }
        };
        if (el.readyState >= 3) {
            attemptPlay();
            return () => { cancelled = true; };
        }
        const onCanPlay = () => {
            el.removeEventListener("canplay", onCanPlay);
            attemptPlay();
        };
        el.addEventListener("canplay", onCanPlay);
        return () => {
            cancelled = true;
            el.removeEventListener("canplay", onCanPlay);
        };
    }, [current, playing]);

    const advance = useCallback(() => {
        setQueue((q) => {
            if (q.length) {
                const [next, ...rest] = q;
                setCurrent(next);
                return rest;
            }
            if (tracks && tracks.length) {
                const [first, ...rest] = _shuffle(tracks);
                setCurrent(first);
                return rest;
            }
            setPlaying(false);
            return [];
        });
    }, [tracks]);

    const togglePlay = useCallback(() => {
        if (!audioRef.current || !current) return;
        if (audioRef.current.paused) {
            audioRef.current.play().then(() => setPlaying(true)).catch((err) => {
                if (err && (err.name === "AbortError"
                            || err.name === "NotAllowedError")) return;
                setError(`playback blocked: ${err?.message || err}`);
            });
        } else {
            audioRef.current.pause();
            setPlaying(false);
        }
    }, [current]);

    const stop = useCallback(() => {
        if (!audioRef.current) return;
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setPlaying(false);
    }, []);

    const toggleMute = useCallback(() => {
        if (!audioRef.current) return;
        const next = !audioRef.current.muted;
        audioRef.current.muted = next;
        setMuted(next);
    }, []);

    return {
        audioRef, current, queue, playing, muted, error,
        togglePlay, stop, toggleMute, advance,
    };
}

export function PlayerPane({ station, tracks, loading }) {
    const {
        audioRef, current, queue, playing, muted, error,
        togglePlay, stop, toggleMute, advance,
    } = usePlayer(tracks);

    return (
        <div data-testid="player-pane">
            {/* Now-playing card */}
            <div
                style={{
                    background: COLOURS.bgSoft,
                    border: `1px solid ${COLOURS.line}`,
                    padding: "24px 24px 20px",
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: COLOURS.accent,
                    }}
                >
                    {loading ? "Buffering" : (playing ? "Now playing" : "Paused")}
                    {station ? ` · ${station.name}` : ""}
                </div>
                <div
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: 28,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                        color: COLOURS.ink,
                        marginTop: 8,
                        minHeight: 40,
                    }}
                    data-testid="now-playing-title"
                >
                    {current?.title || (loading ? "…" : "No track selected")}
                </div>
                {current?.tag === "drop" && (
                    <div
                        style={{
                            display: "inline-block",
                            marginTop: 8,
                            padding: "2px 8px",
                            border: `1px solid ${COLOURS.accent}`,
                            color: COLOURS.accent,
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 10,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                        }}
                    >
                        drop
                    </div>
                )}
                {error && (
                    <div
                        style={{
                            marginTop: 10,
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 11,
                            color: "#e5484d",
                        }}
                        data-testid="player-error"
                    >
                        // {error}
                    </div>
                )}
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                <ControlButton onClick={togglePlay} disabled={!current}
                               active={playing} testid="btn-play">
                    {playing ? "❚❚ Pause" : "▶ Play"}
                </ControlButton>
                <ControlButton onClick={stop} disabled={!current} testid="btn-stop">
                    Stop
                </ControlButton>
                <ControlButton onClick={advance} disabled={!current} testid="btn-skip">
                    Skip
                </ControlButton>
                <div style={{ flex: 1 }} />
                <ControlButton onClick={toggleMute} disabled={!current}
                               active={muted} testid="btn-mute">
                    {muted ? "Unmute" : "Mute"}
                </ControlButton>
            </div>

            <audio
                ref={audioRef}
                onEnded={advance}
                onPlay={() => { /* noop */ }}
                onPause={() => { /* noop */ }}
                crossOrigin="anonymous"
                data-testid="audio-element"
            />
            {/* Iter 31.328 — shared spectrum visualiser. Palette matches
                SpyRadio's steel-blue accent so it fits the same visual
                signature as the internal RadioPlayer page. */}
            <div style={{ marginTop: 20, border: `1px solid ${COLOURS.line}`,
                          background: COLOURS.bgSoft, padding: 12 }}>
                <div style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 10, letterSpacing: "0.24em",
                    textTransform: "uppercase", color: COLOURS.inkSoft,
                    marginBottom: 8,
                }}>
                    signal
                </div>
                <AudioVisualiser
                    audioRef={audioRef}
                    playing={playing}
                    palette={{ accent: COLOURS.accent, accentDim: COLOURS.accentDim }}
                    height={80}
                    testId={`visualiser-${station?.slug || "current"}`}
                />
            </div>
            {/* Iter 31.307 — up-next list uses the live queue from
                usePlayer, filtered to hide drops + capped at 3. */}
            <div style={{ marginTop: 24 }}>
                <TrackList tracks={tracks} current={current} upcoming={queue} />
            </div>
        </div>
    );
}

function ControlButton({ children, onClick, disabled, active, testid }) {
    const [hover, setHover] = useState(false);
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid={testid}
            style={{
                border: `1px solid ${active ? COLOURS.accent : COLOURS.line}`,
                background: hover && !disabled ? COLOURS.bgSoft : "transparent",
                color: disabled ? COLOURS.inkSoft : (active ? COLOURS.accent : COLOURS.ink),
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                padding: "10px 20px",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1,
                transition: "background 180ms, border-color 180ms, color 180ms",
            }}
        >
            {children}
        </button>
    );
}

// ── QUEUE / TRACK LIST ─────────────────────────────────────────────────
// Iter 31.310 — case-insensitive drop detector so any capitalisation
// variance from Drive metadata still filters correctly.
const _isDrop = (t) => {
    const tag = (t?.tag ?? "").toString().toLowerCase();
    return tag === "drop" || tag === "drops";
};
export function TrackList({ tracks, current, upcoming }) {
    if (!tracks || tracks.length === 0) return null;
    // Iter 31.307 — Six's brief: show only the next 3 upcoming tracks,
    // and hide drops from the on-screen list (they still play, just
    // don't clutter the visible queue). `upcoming` is passed from
    // usePlayer's `queue` state; falls back to `tracks` for the
    // per-station page where a queue isn't wired.
    const source = Array.isArray(upcoming) && upcoming.length > 0 ? upcoming : tracks;
    const visible = (source || []).filter((t) => t && !_isDrop(t)).slice(0, 3);
    if (visible.length === 0) return null;
    return (
        <div
            style={{
                background: COLOURS.bgSoft,
                border: `1px solid ${COLOURS.line}`,
                padding: 20,
            }}
            data-testid="track-list"
        >
            <div
                style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 10,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: COLOURS.accent,
                    marginBottom: 14,
                }}
            >
                Up next · {visible.length}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visible.map((t) => {
                    const active = current?.id === t.id;
                    return (
                        <div
                            key={t.id}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontFamily: "'Lexend', system-ui, sans-serif",
                                fontSize: 14,
                                color: active ? COLOURS.accent : COLOURS.ink,
                                padding: "8px 0",
                                borderBottom: `1px solid ${COLOURS.line}`,
                            }}
                            data-testid={`track-row-${t.id}`}
                        >
                            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.title}
                            </span>
                            <span
                                style={{
                                    marginLeft: 12,
                                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                                    fontSize: 10,
                                    letterSpacing: "0.18em",
                                    color: t.tag === "drop" ? COLOURS.accent : COLOURS.inkSoft,
                                    textTransform: "uppercase",
                                }}
                            >
                                {t.tag}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── PAGE ROOT ──────────────────────────────────────────────────────────
export default function SpyRadioMultiStation() {
    useDisplayFonts();
    const [stations, setStations] = useState(null);
    const [stationsErr, setStationsErr] = useState("");
    const [selectedSlug, setSelectedSlug] = useState(null);
    const [tracks, setTracks] = useState(null);
    const [tracksLoading, setTracksLoading] = useState(false);

    // Load station list.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/spyradio/public/stations`);
                if (cancelled) return;
                setStations(data.stations || []);
                // Auto-select first station if any.
                if ((data.stations || []).length) {
                    setSelectedSlug(data.stations[0].slug);
                }
            } catch (err) {
                if (!cancelled) setStationsErr(err?.message || "load failed");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load tracks whenever selection changes.
    useEffect(() => {
        if (!selectedSlug) return;
        let cancelled = false;
        setTracksLoading(true);
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/api/spyradio/public/stations/${encodeURIComponent(selectedSlug)}/tracks`,
                );
                if (cancelled) return;
                setTracks(data.tracks || []);
            } catch {
                if (!cancelled) setTracks([]);
            } finally {
                if (!cancelled) setTracksLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedSlug]);

    const selectedStation = (stations || []).find((s) => s.slug === selectedSlug);

    return (
        <div
            style={{
                background: COLOURS.bg,
                minHeight: "100vh",
                color: COLOURS.ink,
                fontFamily: "'Lexend', system-ui, sans-serif",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
            }}
            data-testid="spyradio-multi-root"
        >
            {/* Top strap */}
            <header
                style={{
                    padding: "48px 6vw 32px",
                    borderBottom: `1px solid ${COLOURS.line}`,
                }}
            >
                <div
                    style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: COLOURS.accent,
                    }}
                >
                    Spy Radio · live
                </div>
                <h1
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: "clamp(40px, 6vw, 80px)",
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        lineHeight: 1.0,
                        margin: "16px 0 0",
                    }}
                >
                    SPY / RADIO
                </h1>
                <p
                    style={{
                        color: COLOURS.inkSoft,
                        marginTop: 14,
                        maxWidth: "56ch",
                        fontSize: 15,
                        lineHeight: 1.55,
                    }}
                >
                    Stations curated by Two. Each folder on Drive is a station.
                    Every session is a shuffled blend of tracks and drops.
                </p>
            </header>

            {/* Body layout */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(240px, 340px) minmax(0, 1fr)",
                    gap: 32,
                    padding: "40px 6vw 80px",
                    alignItems: "start",
                }}
            >
                {/* Station picker column */}
                <aside data-testid="stations-column">
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 10,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: COLOURS.accent,
                            marginBottom: 16,
                        }}
                    >
                        Stations · {stations?.length ?? "…"}
                    </div>
                    {stationsErr && (
                        <div style={{ color: "#e5484d", fontSize: 12 }}>
                            // {stationsErr}
                        </div>
                    )}
                    {stations && stations.length === 0 && (
                        <div
                            style={{
                                border: `1px dashed ${COLOURS.line}`,
                                padding: 20,
                                color: COLOURS.inkSoft,
                                fontSize: 13,
                                lineHeight: 1.55,
                            }}
                            data-testid="stations-empty-state"
                        >
                            No stations on file yet.
                            <br /><br />
                            <span style={{ color: COLOURS.inkSoft, fontSize: 11, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                                Two: create <code style={{ color: COLOURS.accent }}>_Filed/radio_stations/&lt;slug&gt;/{"{tracks,drops}"}</code> on Drive to spin one up.
                            </span>
                        </div>
                    )}
                    <div style={{ display: "grid", gap: 12 }}>
                        {(stations || []).map((s) => (
                            <StationCard
                                key={s.slug}
                                station={s}
                                active={s.slug === selectedSlug}
                                onPick={() => setSelectedSlug(s.slug)}
                                deepLinkTo={`/s/${encodeURIComponent(s.slug)}`}
                            />
                        ))}
                    </div>
                </aside>

                {/* Player column */}
                <main data-testid="player-column">
                    {selectedStation ? (
                        <>
                            <PlayerPane
                                station={selectedStation}
                                tracks={tracks}
                                loading={tracksLoading}
                            />
                            {/* Iter 31.307 — TrackList is now rendered
                                inside PlayerPane so it can consume the
                                live queue (next-3, drops hidden). */}
                        </>
                    ) : (
                        <div
                            style={{
                                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                                color: COLOURS.inkSoft,
                                fontSize: 12,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                            }}
                        >
                            Pick a station on the left to begin.
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
